import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Application, APPLICATION_TYPE_LABELS, EpeFormData, ProjectFormData } from 'app/models/application';
import { TOPIC_GROUPS } from 'app/models/epe-topics';
import { ApplicationsService } from 'app/services/applications.service';

@Component({
  selector: 'app-application-view',
  templateUrl: './application-view.component.html',
  styleUrls: ['./application-view.component.scss']
})
export class ApplicationViewComponent implements OnInit {
  application: Application | null = null;
  loading = true;
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private applicationsService: ApplicationsService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/my-applications']);
      return;
    }
    this.applicationsService.getById(id).subscribe({
      next: (app) => {
        this.application = app;
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'Application not found.';
        this.loading = false;
      }
    });
  }

  get isEpe(): boolean {
    return this.application?.application_type === 'epe';
  }

  get isPss(): boolean {
    return this.application?.application_type === 'service2';
  }

  get serviceTitle(): string {
    const type = this.application?.application_type;
    return (type && APPLICATION_TYPE_LABELS[type]) || type || '';
  }

  get formData(): EpeFormData {
    return (this.application?.form_data as EpeFormData) || {};
  }

  get projectFormData(): ProjectFormData {
    return (this.application?.form_data as ProjectFormData) || {};
  }

  get cvDownloadUrl(): string {
    return `/api/v1/applications/cv-download/${this.formData.cv}`;
  }

  get onePagerDownloadUrl(): string {
    return `/api/v1/applications/cv-download/${this.projectFormData.one_pager_file_id}`;
  }

  private topicValue(t: any): string {
    return typeof t === 'string' ? t : (t?.value ?? '');
  }

  private labelFor(value: string): string {
    for (const group of TOPIC_GROUPS) {
      for (const sub of group.subGroups) {
        if (sub.value === value) return `${group.label} > ${sub.label}`;
        const child = sub.children.find(c => c.value === value);
        if (child) return `${group.label} > ${sub.label} > ${child.label}`;
      }
    }
    return value;
  }

  get topicLabels(): string[] {
    const data = this.isEpe ? this.formData : this.projectFormData;
    const topics: any[] = (data as any).topics || [];
    return topics.map(t => this.labelFor(this.topicValue(t)));
  }

  // EPE only: full per-topic detail when topics are stored as objects.
  get topicDetails(): { label: string; evidence: string; support: string[] }[] {
    const topics: any[] = (this.formData as any).topics || [];
    return topics
      .filter(t => typeof t !== 'string')
      .map(t => ({
        label: this.labelFor(this.topicValue(t)),
        evidence: t.evidence_of_expertise || '',
        support: t.primary_support_types || [],
      }));
  }

  get statusLabel(): string {
    const map: Record<string, string> = {
      pending: 'Pending',
      eligible: 'Eligible',
      not_eligible: 'Not Eligible',
      voted: 'Voted',
      proactive: 'Proactive',
    };
    return map[this.application?.status || ''] || (this.application?.status || '');
  }

  get statusBadgeClass(): string {
    const map: Record<string, string> = {
      pending: 'label-warning',
      eligible: 'label-success',
      not_eligible: 'label-danger',
      voted: 'label-info',
      proactive: 'label-primary',
    };
    return map[this.application?.status || ''] || 'label-default';
  }
}
